import React from 'react';

// Dynamic color system for different communities
export interface CommunityColors {
  brand: string;
  highlight: string;
  accent: string;
  primary?: string;
  secondary?: string;
}

// Color schemes for different communities
export const communityColorSchemes: Record<string, CommunityColors> = {
  'aubrey-tx': {
    brand: '#0f3b96', 
    highlight: '#F59E0B',
    accent: '#E6F0FF',
    primary: '#FFFFFF',
    secondary: '#F3F4F6'
  },
    'baytown-tx': {
    brand: '#2f70e3', 
    highlight: '#F59E0B',
    accent: '#E6F0FF',
    primary: '#FFFFFF',
    secondary: '#F3F4F6'
  },
  'cape-coral': {
    brand: '#6138F5',
    highlight: '#FFE2C2',
    accent: '#E0D8FD',
    primary: '#FFFFFF',
    secondary: '#F3F4F6'
  },
  'kings-mountain': {
    brand: '#7E624E',
    highlight: '#88B1F3',
    accent: '#C0D8C0',
    primary: '#FFFFFF',
    secondary: '#F3F4F6'
  },
  'littleton': {
    brand: '#2C4C5C',
    highlight: '#DEB628',
    accent: '#E2E8F0',
    primary: '#FFFFFF',
    secondary: '#F3F4F6'
  }
};

// Default color scheme (Cape Coral)
export const defaultColorScheme: CommunityColors = communityColorSchemes['cape-coral'];

/**
 * Extract community identifier from URL parameters or embed config
 */
export const getCommunityFromURL = (): string | null => {
  // Check current window first
  let embedCity = (window as any).__VIEWPRO_CITY__;
  console.log('[dynamicColors] Checking current window:', embedCity);
  
  // If not found, check if there's an iframe and look in its window
  if (!embedCity) {
    const iframe = document.querySelector('iframe[srcdoc]') as HTMLIFrameElement;
    if (iframe && iframe.contentWindow) {
      embedCity = (iframe.contentWindow as any).__VIEWPRO_CITY__;
      console.log('[dynamicColors] Checking iframe window:', embedCity);
    }
  }
  
  console.log('[dynamicColors] Final embed city:', embedCity);
  console.log('[dynamicColors] Available schemes:', Object.keys(communityColorSchemes));
  
  if (embedCity && communityColorSchemes[embedCity]) {
    console.log('[dynamicColors] ✅ Using embed city:', embedCity);
    return embedCity;
  }
  
  // Fall back to URL parameters (for standalone mode)
  const urlParams = new URLSearchParams(window.location.search);
  console.log('[dynamicColors] Checking URL params:', window.location.search);
  
  // Check for direct parameter like ?aubrey-tx
  const keys = Array.from(urlParams.keys());
  for (const key of keys) {
    if (communityColorSchemes[key]) {
      return key;
    }
  }
  
  // Check for parameter values like ?community=aubrey-tx
  const community = urlParams.get('community');
  if (community && communityColorSchemes[community]) {
    return community;
  }
  
  return null;
};

/**
 * Apply color scheme to CSS custom properties
 */
export const applyCommunityColors = (communityId: string | null): void => {
  const colorScheme = communityId && communityColorSchemes[communityId] 
    ? communityColorSchemes[communityId] 
    : defaultColorScheme;
  
  const root = document.documentElement;
  
  // Apply the colors to CSS custom properties
  root.style.setProperty('--color-brand', colorScheme.brand);
  root.style.setProperty('--color-highlight', colorScheme.highlight);
  root.style.setProperty('--color-accent', colorScheme.accent);
  
  if (colorScheme.primary) {
    root.style.setProperty('--color-primary', colorScheme.primary);
  }
  
  if (colorScheme.secondary) {
    root.style.setProperty('--color-secondary', colorScheme.secondary);
  }
  
  // Add a data attribute to the body for community-specific styling
  document.body.setAttribute('data-community', communityId || 'default');
  
  console.log(`Applied color scheme for: ${communityId || 'default'}`, colorScheme);
};

/**
 * Initialize dynamic colors based on current URL
 */
export const initializeDynamicColors = (): void => {
  const community = getCommunityFromURL();
  applyCommunityColors(community);
};

/**
 * React hook for dynamic colors
 */
export const useDynamicColors = () => {
  const [currentCommunity, setCurrentCommunity] = React.useState<string | null>(null);
  
  React.useEffect(() => {
    const community = getCommunityFromURL();
    setCurrentCommunity(community);
    applyCommunityColors(community);
    
    // Listen for URL changes (for SPA navigation)
    const handlePopState = () => {
      const newCommunity = getCommunityFromURL();
      setCurrentCommunity(newCommunity);
      applyCommunityColors(newCommunity);
    };
    
    window.addEventListener('popstate', handlePopState);
    
    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, []);
  
  return {
    currentCommunity,
    colorScheme: currentCommunity ? communityColorSchemes[currentCommunity] : defaultColorScheme,
    applyCommunityColors
  };
};
